<script setup>
import { ref, computed, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import WhatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  TEMPLATE_LANGUAGES,
  TEMPLATE_CATEGORIES,
  normalizeVariableName,
  bodyVariables,
  insertVariableToken,
  bodyRuleViolations,
  nameRuleViolations,
  exampleRuleViolations,
  buildBodyComponent,
} from './templateComposer';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  existingTemplates: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['created', 'close']);

const { t } = useI18n();
const store = useStore();

// Render the `{{name}}` placeholder as plain text (kept out of the mustache
// source so the Vue compiler does not treat the braces as an interpolation).
const formatVariable = variable => `{{${variable}}}`;

const name = ref('');
const language = ref('es_MX');
const category = ref('UTILITY');
const bodyText = ref('');
const exampleValues = ref({});
const isCreating = ref(false);
const bodyInput = ref(null);

// WhatsApp template names: lowercase letters, numbers and underscores only.
const validName = helpers.regex(/^[a-z0-9_]+$/);

const rules = {
  name: { required, validName },
  language: { required },
  category: { required },
  bodyText: { required },
};

const v$ = useVuelidate(rules, { name, language, category, bodyText });

// --- Quick variable buttons (contact fields + custom attributes) ----------
const attributesByModel = useMapGetter('attributes/getAttributesByModel');

onMounted(() => store.dispatch('attributes/get'));

const quickFields = computed(() => {
  const standard = [
    {
      key: 'nombre_contacto',
      label: t(
        'INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.FORM.VARIABLES.CONTACT_NAME'
      ),
    },
    {
      key: 'correo',
      label: t(
        'INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.FORM.VARIABLES.CONTACT_EMAIL'
      ),
    },
    {
      key: 'telefono',
      label: t(
        'INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.FORM.VARIABLES.CONTACT_PHONE'
      ),
    },
  ];
  const custom = attributesByModel
    .value('contact_attribute')
    .map(attribute => ({
      key: normalizeVariableName(attribute.attribute_key),
      label: attribute.attribute_display_name,
    }));
  return [...standard, ...custom];
});

const insertField = async field => {
  const cursor = bodyInput.value?.selectionStart ?? bodyText.value.length;
  const { text, cursor: newCursor } = insertVariableToken(
    bodyText.value,
    cursor,
    field.key
  );
  bodyText.value = text;
  // Suggest the field label as the example value Meta requires (editable).
  if (!(exampleValues.value[field.key] || '').trim()) {
    exampleValues.value[field.key] = field.label;
  }
  v$.value.bodyText.$touch();
  await nextTick();
  bodyInput.value?.focus();
  bodyInput.value?.setSelectionRange(newCursor, newCursor);
};

// --- Meta compliance guardrails --------------------------------------------
const variables = computed(() => bodyVariables(bodyText.value));

const ruleViolations = computed(() => [
  ...bodyRuleViolations(bodyText.value),
  ...nameRuleViolations(name.value, language.value, props.existingTemplates),
  ...exampleRuleViolations(variables.value, exampleValues.value),
]);

const isCompliant = computed(() => ruleViolations.value.length === 0);
// ---------------------------------------------------------------------------

const createTemplate = async () => {
  v$.value.$touch();
  if (v$.value.$invalid || !isCompliant.value) return;

  isCreating.value = true;
  try {
    const payload = {
      name: name.value,
      language: language.value,
      category: category.value,
      components: [buildBodyComponent(bodyText.value, exampleValues.value)],
    };
    if (variables.value.length) payload.parameter_format = 'NAMED';

    const { data } = await WhatsappTemplatesAPI.create(props.inbox.id, payload);
    emit('created', data);
  } catch (error) {
    const metaError = error?.response?.data?.error;
    useAlert(metaError || t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.ERROR'));
  } finally {
    isCreating.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.TITLE')"
      :header-content="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.DESC')"
    />
    <form
      class="flex flex-col items-start w-full"
      @submit.prevent="createTemplate"
    >
      <div class="w-full">
        <label :class="{ error: v$.name.$error }">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.FORM.NAME.LABEL') }}
          <input
            v-model="name"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.FORM.NAME.PLACEHOLDER')
            "
            @input="v$.name.$touch"
          />
          <span v-if="v$.name.$error" class="message">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.FORM.NAME.ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex w-full gap-3">
        <div class="w-1/2">
          <label :class="{ error: v$.language.$error }">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.FORM.LANGUAGE.LABEL') }}
            <select v-model="language" @change="v$.language.$touch">
              <option
                v-for="lang in TEMPLATE_LANGUAGES"
                :key="lang.value"
                :value="lang.value"
              >
                {{ lang.label }}
              </option>
            </select>
          </label>
        </div>
        <div class="w-1/2">
          <label :class="{ error: v$.category.$error }">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.FORM.CATEGORY.LABEL') }}
            <select v-model="category" @change="v$.category.$touch">
              <option
                v-for="cat in TEMPLATE_CATEGORIES"
                :key="cat"
                :value="cat"
              >
                {{ cat }}
              </option>
            </select>
          </label>
        </div>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.bodyText.$error }">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.FORM.BODY.LABEL') }}
          <textarea
            ref="bodyInput"
            v-model="bodyText"
            rows="4"
            :placeholder="
              $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.FORM.BODY.PLACEHOLDER')
            "
            @input="v$.bodyText.$touch"
          />
        </label>
        <div class="w-full -mt-1 mb-2">
          <p class="mb-1.5 text-xs text-n-slate-11">
            {{
              $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.FORM.VARIABLES.LABEL')
            }}
          </p>
          <div class="flex overflow-x-auto gap-2 pb-1.5 whitespace-nowrap">
            <Button
              v-for="field in quickFields"
              :key="field.key"
              xs
              faded
              slate
              type="button"
              icon="i-lucide-braces"
              class="flex-shrink-0"
              :label="field.label"
              @click.prevent="insertField(field)"
            />
          </div>
        </div>
        <p class="text-xs text-slate-500 dark:text-slate-400 -mt-2">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.FORM.BODY.HINT') }}
        </p>
      </div>

      <div v-if="variables.length" class="w-full">
        <p class="text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.FORM.EXAMPLES.LABEL') }}
        </p>
        <div v-for="variable in variables" :key="variable" class="w-full">
          <label>
            {{ formatVariable(variable) }}
            <input
              v-model="exampleValues[variable]"
              type="text"
              :placeholder="
                $t(
                  'INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.FORM.EXAMPLES.PLACEHOLDER',
                  { variable }
                )
              "
            />
          </label>
        </div>
      </div>

      <div
        v-if="ruleViolations.length"
        class="w-full px-3 py-2.5 mb-2 rounded-lg border border-n-amber-5 bg-n-amber-2"
      >
        <p
          class="flex gap-1.5 items-center mb-1 text-xs font-medium text-n-amber-11"
        >
          <Icon
            icon="i-lucide-shield-alert"
            class="w-3.5 h-3.5 flex-shrink-0"
          />
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.RULES.TITLE') }}
        </p>
        <ul class="mb-0 pl-5 list-disc text-xs text-n-amber-11">
          <li v-for="violation in ruleViolations" :key="violation">
            {{ $t(`INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.RULES.${violation}`) }}
          </li>
        </ul>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.CANCEL')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.SUBMIT')"
          :disabled="v$.$invalid || !isCompliant || isCreating"
          :is-loading="isCreating"
        />
      </div>
    </form>
  </div>
</template>
